import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { useDispatch } from 'react-redux';
import { showModal } from '../../../../../Actions/PopUp';
import { IconAlertHexagon, loaderGIF, SelectedClose } from '../../../../../assets/images';
import { navigateChannel } from '../../../../../Components/Layouts/Sidebar';
import { MFChannel } from '../../../../../Components/SDK';
import { getAppOnlineStatus, setActiveConversationChannel } from '../../../../../Slice';
import Store from '../../../../../Store';
import { ChannelName } from '../../../../components';
import ChannelCreated from '../../../../components/ChannelProfileInfo/ChannelCreated';
import { ChannelStatusMode } from '../../../../components/ChannelProfileInfo/ChannelStatus';
import useChannelInfo from '../../../../hooks/useChannelInfo';
import { showErrorToast, showSucessToast } from '../../../../utils';
import { ChannelAvatar } from './ChannelAvatar';
import './JoinRequest.scss';
import { SmartButton } from './SmartButton';
import toastr from 'toastr';
import { NO_INTERNET } from '../../../../utils/constants';

const STATUS_MESSAGES = {
   INACTIVE: 'This channel is deactivated. Join when reactivated by admin.',
   DELETED: 'The channel has been deleted by Channel Admin',
};

const MODAL_TYPES = {
   JOIN_REQUEST: 'joinRequest',
};

const CloseButton = memo(({ onClick }) => (
   <button
      className="close_popup"
      onClick={onClick}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      aria-label="Close popup">
      <SelectedClose />
   </button>
));

CloseButton.displayName = 'CloseButton';
CloseButton.propTypes = {
   onClick: PropTypes.func.isRequired,
};

const LoadingSpinner = memo(() => (
   <div className="loader-wrapper">
      <img src={loaderGIF} alt="Loading..." style={{ width: '50px', height: '50px' }} />
   </div>
));

LoadingSpinner.displayName = 'LoadingSpinner';

const ErrorModal = memo(({ error, onClose }) => (
   <div className="alert-modal-backdrop">
      <div className="alert-modal-content" style={{ width: '400px' }}>
         <div className="alert-modal-icon">
            <IconAlertHexagon />
         </div>
         <h3 className="alert-modal-title">OOPS!!</h3>
         <p className="alert-modal-description">{error.message || 'Something went wrong'}</p>
         <div className="alert-modal-buttons-container">
            <div className="alert-modal-buttons">
               <button className="alert-button" onClick={onClose}>
                  CLOSE
               </button>
            </div>
         </div>
      </div>
   </div>
));

ErrorModal.displayName = 'ErrorModal';
ErrorModal.propTypes = {
   error: PropTypes.object.isRequired,
   onClose: PropTypes.func.isRequired,
};

const ChannelCard = memo(({ channelId, statusMessage, onRequestJoin }) => (
   <div className="channel-card">
      <ChannelAvatar channelId={channelId} />

      <div className="channel-info">
         <ChannelName channelId={channelId} className="channel-name" />
         <ChannelCreated channelId={channelId} />
      </div>

      <ChannelStatusMode channelId={channelId} />

      {statusMessage ? (
         <p className="channel-status">{statusMessage}</p>
      ) : (
         <SmartButton className="request-button" isLoading={false} onClick={onRequestJoin} channelId={channelId} />
      )}
   </div>
));

ChannelCard.displayName = 'ChannelCard';
ChannelCard.propTypes = {
   channelId: PropTypes.string.isRequired,
   statusMessage: PropTypes.string,
   onRequestJoin: PropTypes.func.isRequired,
};

const JoinRequest = memo(({ channelId: channelID, accessUri = '', onClose }) => {
   const [channelId, setChannelId] = useState(channelID);
   const { info, isLoading, error } = useChannelInfo({ channelId: channelID, accessUri });
   const dispatch = useDispatch();

   const handleClosePopup = useCallback(() => {
      if (onClose) {
         onClose();
      } else {
         Store.dispatch(
            showModal({
               open: false,
               modelType: MODAL_TYPES.JOIN_REQUEST,
            }),
         );
      }
   }, [onClose]);

   useEffect(() => {
      if (info) {
         setChannelId(info.channelId);
         const member = info.member;
         if (member && Object.keys(member).length > 0 && member.status !== 0) {
            navigateChannel?.current?.toRecentChannels();
            dispatch(setActiveConversationChannel(info));
            handleClosePopup();
         }
      }
   }, [info, dispatch, handleClosePopup]);

   const handleRequestJoin = useCallback(
      async value => {
         try {
            if (!getAppOnlineStatus()) return toastr.error(NO_INTERNET);
            const isJoin = value === 'Join';
            const action = isJoin ? MFChannel.joinChannel : MFChannel.joinPrivateChannel;

            const res = await action({ channelId });
            if (res.statusCode !== 200) {
               showErrorToast(res?.message || 'Failed to process request');
               return;
            }
            const message = isJoin ? 'You have successfully joined the channel' : 'Request sent to join the channel';
            showSucessToast(message);
            if (isJoin) {
               setTimeout(() => {
                  navigateChannel?.current?.toRecentChannels();
                  dispatch(setActiveConversationChannel({ channelId }));
               }, 300);
            }
            handleClosePopup();
         } catch (error) {
            console.error('Error sending join request:', error);
            showErrorToast('Network error occurred. Please try again.');
         }
      },
      [dispatch, channelId, handleClosePopup],
   );

   const statusMessage = useMemo(() => {
      return STATUS_MESSAGES[info?.status] || null;
   }, [info?.status]);

   const mainContent = useMemo(() => {
      if (isLoading) {
         return <LoadingSpinner />;
      }

      if (!channelId) {
         return null;
      }

      return (
         <div className="join-request-popup">
            <CloseButton onClick={handleClosePopup} />
            <ChannelCard channelId={channelId} statusMessage={statusMessage} onRequestJoin={handleRequestJoin} />
         </div>
      );
   }, [channelId, isLoading, statusMessage, handleClosePopup, handleRequestJoin]);

   if (error) {
      return <ErrorModal error={error} onClose={handleClosePopup} />;
   }

   return (
      <div className="popup-wrapper channel-popup">
         <div className="join-request">
            <div className="join-request-wrapper">
               <div className="popup-container-inner">{mainContent}</div>
            </div>
         </div>
      </div>
   );
});

JoinRequest.displayName = 'JoinRequest';
JoinRequest.propTypes = {
   channelId: PropTypes.string,
   accessUri: PropTypes.string,
   onClose: PropTypes.func,
};

export default JoinRequest;
